import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SectionHeading, Reveal } from './_shared';
import { useListingCounts, type CountSpec } from '../hooks/useListingCounts';
import { cityCardHref, cityCardSpan, cityCountRequest } from '../cityCards';
import { useSaudiCities } from '../../../hooks/useSaudiCities';
import { CITY_PHOTOS } from '../../../config/photoCredits';
import type { FeaturedCitySlug } from '../../../config/saudiCities';
import { formatAmount } from '../../../utils/numberFormat';

// The owner's featured cities in the owner's order (config/saudiCities:
// مكة المكرمة، المدينة المنورة، الرياض، جدة، الدمام), each with its landmark
// photo. Every photo is credited at /about#photo-credits (config/photoCredits).
// Counts and links filter by the database `cityId` when it is known, so the
// number on a card is the number of results behind it.
export default function ExploreByCity() {
  const { t, i18n } = useTranslation(['home']);
  const isRTL = i18n.dir() === 'rtl';
  const isArabic = i18n.language?.startsWith('ar') ?? isRTL;
  const arrow = isRTL ? 'ri-arrow-left-up-line' : 'ri-arrow-right-up-line';
  const { featured, loading } = useSaudiCities();

  // Wait for the database ids before counting: counting by name first and
  // again by id would double the requests for the same five numbers.
  const specs: CountSpec[] = useMemo(
    () => (loading ? [] : featured.map((city) => ({ key: city.slug, request: cityCountRequest(city) }))),
    [featured, loading],
  );
  const counts = useListingCounts(specs);

  return (
    <section className="bg-white py-14 sm:py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading title={t('home.v2.cities.title')} isRTL={isRTL} />
        {/* role="list": Safari drops list semantics from `list-style: none` lists. */}
        <ul role="list" className="grid grid-cols-2 gap-3 sm:grid-cols-6 sm:gap-4 lg:grid-cols-5">
          {featured.map((city, i) => {
            const photo = CITY_PHOTOS[city.slug as FeaturedCitySlug];
            const count = counts[city.slug];
            const name = t(`home.v2.cities.names.${city.slug}`, { defaultValue: isArabic ? city.nameAr : city.nameEn });
            return (
              <li key={city.slug} className={cityCardSpan(i, featured.length)}>
                <Reveal delay={i * 0.06} className="h-full">
                  <Link
                    to={cityCardHref(city, isArabic)}
                    // Flat teal behind the photo while it loads; no gradient
                    // band (THEME.md §3 rule 4). The shadow is teal-tinted, as
                    // on every other card on the page.
                    className="group relative flex h-40 flex-col justify-end overflow-hidden rounded-2xl bg-[#004D40] p-4 text-white shadow-[0_18px_40px_-24px_rgba(0,77,64,0.55)] transition-transform hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)] focus-visible:ring-offset-2 motion-reduce:transition-none motion-reduce:hover:translate-y-0 sm:h-44 sm:p-5"
                  >
                    {photo && (
                      <img
                        src={photo.src}
                        alt=""
                        aria-hidden="true"
                        width={photo.width}
                        height={photo.height}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                      />
                    )}
                    {/* Neutral legibility scrim under the white name, not a
                        green wash: a photograph keeps its own colour and the
                        brand arrives through type and marks (THEME.md §8). */}
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"
                    />
                    {/* A solid chip, not glass: a 2rem tile around a 1rem arrow. */}
                    <span
                      className="absolute end-3 top-3 flex h-8 w-8 items-center justify-center rounded-lg bg-white text-base text-[#004D40] shadow-sm transition-colors group-hover:bg-[#F2F8F7]"
                      aria-hidden="true"
                    >
                      <i className={arrow} />
                    </span>
                    <span className="relative font-display text-xl font-bold drop-shadow-sm">{name}</span>
                    {typeof count === 'number' ? (
                      <span className="relative mt-0.5 text-sm text-white/90 tabular-nums">
                        {t('home.v2.cities.count', { count, n: formatAmount(count) })}
                      </span>
                    ) : count === null ? (
                      <span className="relative mt-1 h-4 w-20 animate-pulse rounded bg-white/20 motion-reduce:animate-none" aria-hidden="true" />
                    ) : null}
                  </Link>
                </Reveal>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
